import React, { useCallback, useMemo } from 'react';
import MDEditor, { commands } from '@uiw/react-md-editor';
import Compressor from 'compressorjs';

import './styles.css';

// Compress image before upload
const compressImage = (file) => {
	return new Promise((resolve, reject) => {
		new Compressor(file, {
			quality: 0.7, // 70% quality
			maxWidth: 1200, // Max width 1200px
			maxHeight: 1200, // Max height 1200px
			convertSize: 100000, // Convert to JPEG if over 100KB
			convertTypes: ['image/png', 'image/webp'],
			success: resolve,
			error: reject,
		});
	});
};

const MarkdownEditor = ({
	value,
	onChange,
	placeholder = 'Enter description...',
	className = '',
	disableImages = false,
	compact = false,
	uploadImage, // Function to upload image, should return { url: string }
}) => {
	const handleImageUpload = useCallback(async () => {
		if (!uploadImage) return;

		const input = document.createElement('input');
		input.setAttribute('type', 'file');
		input.setAttribute('accept', 'image/*');
		input.click();

		input.onchange = async () => {
			const file = input.files[0];
			if (!file) return;

			try {
				// Compress image before upload
				const compressedFile = await compressImage(file);

				// Upload compressed image to server
				const result = await uploadImage(compressedFile);

				// Insert markdown image syntax at cursor
				const imageMarkdown = `![${file.name}](${result.url})`;
				const newValue = value ? `${value}\n\n${imageMarkdown}` : imageMarkdown;
				onChange(newValue);
			} catch (error) {
				console.error('Failed to upload image:', error);
				alert('Failed to upload image. Please try again.');
			}
		};
	}, [uploadImage, value, onChange]);

	// Custom image command
	const imageCommand = useMemo(
		() => ({
			name: 'image',
			keyCommand: 'image',
			buttonProps: { 'aria-label': 'Insert image' },
			icon: (
				<svg width="12" height="12" viewBox="0 0 20 20">
					<path
						fill="currentColor"
						d="M15 9c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm4-7H1c-.55 0-1 .45-1 1v14c0 .55.45 1 1 1h18c.55 0 1-.45 1-1V3c0-.55-.45-1-1-1zm-1 13l-6-5-2 2-4-5-4 8V4h16v11z"
					/>
				</svg>
			),
			execute: handleImageUpload,
		}),
		[handleImageUpload]
	);

	// Build toolbar commands
	const toolbarCommands = useMemo(() => {
		const baseCommands = [
			commands.group(
				[commands.title1, commands.title2, commands.title3],
				{
					name: 'title',
					groupName: 'title',
					buttonProps: { 'aria-label': 'Insert title' },
				}
			),
			commands.bold,
			commands.italic,
			commands.strikethrough,
			commands.divider,
			commands.orderedListCommand,
			commands.unorderedListCommand,
			commands.divider,
			commands.link,
		];

		if (!disableImages && uploadImage) {
			baseCommands.push(imageCommand);
		}

		return baseCommands;
	}, [disableImages, uploadImage, imageCommand]);

	const height = compact ? 120 : 200;

	return (
		<div
			className={`markdown-editor ${compact ? 'compact' : ''} ${className}`}
			data-color-mode="light"
		>
			<MDEditor
				value={value || ''}
				onChange={onChange}
				preview="edit"
				commands={toolbarCommands}
				extraCommands={[]}
				textareaProps={{
					placeholder,
				}}
				height={height}
				visibleDragbar={false}
			/>
		</div>
	);
};

export default MarkdownEditor;
